import { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, useHistory, useLocation } from 'react-router-dom';
import { getMe } from '../../API/services/authService';
import { auth0Client } from '../../auth0/auth0';
import { ErrorType } from '../../constants/enums';
import { HOME, REGISTER } from '../../constants/routes';
import useWindowName from '../../hooks/useWindowName';
import LoggedLayout from '../../layout/LoggedLayout';
import LoggedOutLayout from '../../layout/LoggedOutLayout';
import NotApprovedLayout from '../../layout/NotApprovedLayout/NotApprovedLayout';
import NotApproved from '../../pages/NotApproved';
import { checkToken, setUser } from '../../redux/actions/authActions';
import { TrackEventGTM } from '../../utils/filters/analyticsUtils';
import { Window } from '../../utils/window';
import Spinner from '../spinner/Spinner';
import LayoutSkeleton from '../LayoutSkeleton/LayoutSkeleton';
import HomeUserProfileSkeleton from '../HomeUserProfile/HomeUserProfileSkeleton';
import HomePlanProfileSkeleton from '../HomePlanProfile/HomePlanProfileSkeleton';
import { EventsGTM } from '../../constants/events';
import { clearLocalStorageOnLogout } from '../../utils/localStorageUtils';

function CheckToken({ children }) {
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!auth.user) {
      dispatch(checkToken());
    }
  }, []);

  return (
    <>
      {auth.isChecking ? (
        <div className="d-flex justify-content-center align-items-center min-vh-100">
          <Spinner show />
        </div>
      ) : (
        children
      )}
    </>
  );
}

function PrivateRoute({ children, ...rest }) {
  const isLoading = useSelector((state) => state.auth.isLoadingMe);
  const isAuthenticated = useSelector((state) => state.auth.authenticated);
  const [loading, setLoading] = useState(false);
  const { user, currentWorkspace } = useSelector((state) => state.auth);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const isMountedRef = useRef(true);

  const { windowName } = useWindowName();

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const logout = () => {
    if (Window()) {
      clearLocalStorageOnLogout();
      auth0Client().logout({ returnTo: window.location.origin });
    }
  };
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      setLoading(true);
      getMe()
        .then((user) => {
          if (!user) logout();
          if (isMountedRef.current) {
            dispatch(setUser(user));
          }
        })
        .catch((error) => {
          TrackEventGTM(EventsGTM.ERROR_LOGIN_PARTNER, {
            event_action: 'login',
            event_category: 'Error',
            event_label: 'Rol invalid',
            userEmail: 'notLoginEmail',
            error,
          });
          if (isMountedRef.current) {
            history.push(`/error?type=${ErrorType.INVALID_ROLES}`);
          }
        })
        .finally(() => {
          if (isMountedRef.current) {
            setLoading(false);
          }
        });
    }
    if (!isAuthenticated && isLoading) {
      const urlParams = new URLSearchParams(window.location.search);
      const workspaceId = urlParams.get('workspaceId');
      if (workspaceId && workspaceId !== currentWorkspace) {
        localStorage.setItem('workspaceIdRedirect', workspaceId);
      }
    }
  }, [isAuthenticated, isLoading]);

  return (
    <>
      <Helmet>
        <title>{windowName}</title>
      </Helmet>
      <Route
        {...rest}
        render={() => {
          switch (true) {
            case (isLoading || loading) && user === null:
              return (
                <LayoutSkeleton>
                  <div className="home-container">
                    <div className="home-display-content">
                      <div className="d-block d-lg-flex bg-white justify-content-center py-2 w-100 justify-self-center rounded-top position-relative z-index-0 mt-4">
                        <HomeUserProfileSkeleton />
                        <div className="home-profile-separator" />
                        <HomePlanProfileSkeleton />
                      </div>
                    </div>
                  </div>
                </LayoutSkeleton>
              );
            case isAuthenticated && Boolean(user?.company?.id):
              return <LoggedLayout>{children}</LoggedLayout>;
            case isAuthenticated &&
              user !== null &&
              !user?.company?.id &&
              location.pathname === HOME:
              return (
                <NotApprovedLayout>{user ? <NotApproved /> : <LayoutSkeleton />}</NotApprovedLayout>
              );
            case !isAuthenticated && !isLoading:
              return <Redirect to={REGISTER} />;
            default:
              return null;
          }
        }}
      />
    </>
  );
}

function LoggedOutRoute({ children, ...rest }) {
  const isLoading = useSelector((state) => state.auth.isLoadingMe);
  const { authenticated, isVerify } = useSelector((state) => state.auth);

  const location = useLocation();
  useEffect(() => window.scrollTo(0, 0), [location.pathname]);
  return (
    <Route
      {...rest}
      render={() =>
        isLoading ? (
          <LoggedOutLayout>
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: '400px' }}
            >
              <div style={{ width: '400px' }}>
                <Spinner show />
              </div>
            </div>
          </LoggedOutLayout>
        ) : authenticated && isVerify ? (
          <Redirect to={HOME} />
        ) : (
          <LoggedOutLayout>{children}</LoggedOutLayout>
        )
      }
    />
  );
}

function PublicRoute({ children, ...rest }) {
  const auth = useSelector((state) => state.auth);
  const location = useLocation();
  useEffect(() => window.scrollTo(0, 0), [location.pathname]);
  return (
    <Route
      {...rest}
      render={() => (auth.token && auth.user ? <LoggedLayout>{children}</LoggedLayout> : children)}
    />
  );
}
function FreeRoute({ children, ...rest }) {
  return <Route {...rest} render={() => children} />;
}

export { CheckToken, FreeRoute, LoggedOutRoute, PrivateRoute, PublicRoute };
